import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import type { ReactNode } from 'react';
import { PAGE_SIZE } from '../defaults';
import { useActions } from '../application/actions/use-actions';
import {
    collapsibleGroupKeys,
    filterCollapsedColumns,
    useColumnGroups,
} from '../application/columns/use-column-groups';
import {
    resolveAllColumns,
    useColumns,
} from '../application/columns/use-columns';
import { useAdapter } from '../application/data/use-adapter';
import { useControlTarget } from '../application/expansion/use-control-target';
import { useExpansion } from '../application/expansion/use-expansion';
import { useSubTable } from '../application/expansion/use-sub-table';
import { useSettings } from '../application/layout/use-settings';
import { useLockedFilters } from '../application/query/use-locked-filters';
import { useQueryPersistence } from '../application/query/use-query-persistence';
import { useUrlLockedFilters } from '../application/query/use-url-locked-filters';
import { useSelection } from '../application/selection/use-selection';
import { useUrlState } from '../application/url/use-url-state';
import type {
    EmptyStateConfig,
    FilterModel,
    ResolvedColumn,
    ResolvedFilter,
    SortState,
    TableAction,
    TableApi,
    TableProps,
} from '../domain';
import { moveItem } from '../domain/shared/move-item';
import type { TableAdapter } from '../ports';
import { TableView } from './table-view/TableView';
import type {
    DragConfig,
    ExpandConfig,
    SelectConfig,
} from './table-view/TableView';
import { TableToolbar, RowMenu } from './toolbar/Toolbar';
import { Empty, Failed, StaleError } from './table-view/Empty';
import { TableSearch } from './search/Search';
import { TablePagination } from './pagination/Pagination';
import { ColumnChooser } from './column-chooser/ColumnChooser';

interface InternalTableProps<T> extends TableProps<T> {
    __nested?: boolean;
    arrayAdapter?: <U>(rows: U[]) => TableAdapter<U>;
}

function filtersToModel(filters: ResolvedFilter[]): FilterModel {
    const model: FilterModel = {};
    filters.forEach((f) => {
        model[f.key] = f;
    });
    return model;
}

function modelToFilters(model: FilterModel): ResolvedFilter[] {
    return Object.entries(model).map(([key, filter]) => ({ ...filter, key }));
}

function nextSort(current: SortState | null, key: string): SortState | null {
    if (!current || current.key !== key) return { key, dir: 'asc' };
    return current.dir === 'asc' ? { key, dir: 'desc' } : null;
}

function defaultGetRowId<T>(row: T): string {
    const id = (row as { id?: unknown } | null | undefined)?.id;
    if (id === undefined) {
        throw new Error(
            'ics-data-table: row has no `id` — pass `getRowId` explicitly.',
        );
    }
    return id as string;
}

function emptyContent(
    error: unknown,
    hasRows: boolean,
    cfg: EmptyStateConfig | undefined,
    filtered: boolean,
    onRetry: () => void,
): ReactNode {
    if (error && !hasRows) return <Failed error={error} onRetry={onRetry} />;
    return <Empty config={cfg} filtered={filtered} />;
}

function TableInner<T>(
    props: InternalTableProps<T>,
    ref: React.Ref<TableApi<T>>,
): JSX.Element {
    const {
        id,
        description,
        columns: defs,
        adapter: rawAdapter,
        getRowId = defaultGetRowId,
        __nested,
        urlSync,
        arrayAdapter,
    } = props;
    const {
        selection: selectionMode = false,
        selectable,
        onSelectionChange,
    } = props;
    const { subTable, actions, emptyState, onRowClick, onRowDragEnd } = props;
    const { toolbarSlot, hideSearch, searchPlaceholder } = props;

    const url = !urlSync || __nested ? false : urlSync;

    const settings = useSettings({ id });
    const snapshot = useAdapter(rawAdapter);
    const allCols = useMemo(
        () => resolveAllColumns(defs, snapshot.capabilities),
        [defs, snapshot.capabilities],
    );

    const lockedFilters = useUrlLockedFilters(id, url, allCols);
    const { adapter, userFilters } = useLockedFilters(
        rawAdapter,
        lockedFilters,
    );
    adapter.applyDefaultPageSize?.(PAGE_SIZE.table);

    const columns = useColumns({
        defs,
        caps: snapshot.capabilities,
        layout: settings.layout,
    });
    const visibleColumnFields = useMemo(
        () => columns.flatMap((c) => (c.field ? [c.field] : [])),
        [columns],
    );
    const appliedVisibleFieldsRef = useRef<string[]>();
    if (appliedVisibleFieldsRef.current !== visibleColumnFields) {
        adapter.setVisibleFields?.(visibleColumnFields);
        appliedVisibleFieldsRef.current = visibleColumnFields;
    }
    const hiddenIds = allCols
        .filter((c) => !columns.some((rc) => rc.id === c.id))
        .map((c) => c.id);

    useQueryPersistence({
        adapter,
        columns: allCols,
        snapshot,
        settings,
        userFilters,
    });

    useUrlState({ id, adapter, columns: allCols, snapshot, url, userFilters });

    const visibleFieldsKey = visibleColumnFields.join('|');
    const supportsVisibleFields =
        typeof adapter.setVisibleFields === 'function';
    const isFirstVisibilityRun = useRef(true);
    useEffect(() => {
        if (!supportsVisibleFields) return;
        if (isFirstVisibilityRun.current) {
            isFirstVisibilityRun.current = false;
            return;
        }
        adapter.refresh();
    }, [visibleFieldsKey, adapter, supportsVisibleFields]);

    const groupCollapse = useColumnGroups();
    const collapsibleGroups = useMemo(() => collapsibleGroupKeys(defs), [defs]);
    const gridColumns = useMemo(
        () => filterCollapsedColumns(columns, groupCollapse.isCollapsed),
        [columns, groupCollapse.isCollapsed],
    );

    const selection = useSelection({
        rows: snapshot.rows,
        getRowId,
        mode: selectionMode,
        selectable,
        onChange: (rows, ids) => {
            onSelectionChange?.(rows, ids);
            if (rows.length > 0) expansion.collapse();
        },
    });
    const expansion = useExpansion({
        ids: snapshot.rows.map(getRowId),
        onExpand: selection.clear,
    });

    const { childAdapter, childSelectedRows, setChildSelectedRows } =
        useSubTable({
            subTable,
            expandedId: expansion.id,
            rows: snapshot.rows,
            getRowId,
            toAdapter: (data) => {
                if (Array.isArray(data) && !arrayAdapter) {
                    throw new Error(
                        'ics-data-table: subTable.data returned an array, but no `arrayAdapter` was supplied — import `IcsDataTable` from the package root, which supplies it.',
                    );
                }
                const child = (
                    Array.isArray(data) ? arrayAdapter?.(data) : data
                ) as TableAdapter<unknown>;
                child.applyDefaultPageSize?.(PAGE_SIZE.sub);
                return child;
            },
        });
    const rowExpanded = expansion.id !== null;

    const api: TableApi<T> = {
        getSelectedRows: () => selection.rows,
        clearSelection: selection.clear,
        setSelected: selection.set,
        getRows: () => snapshot.rows,
        refresh: adapter.refresh,
        getFilters: () => filtersToModel(adapter.getSnapshot().filters),
        setFilters: (model) => {
            adapter.setFilters(modelToFilters(model));
        },
        collapseAll: expansion.collapse,
        getRowCount: () => adapter.getSnapshot().rows.length,
        getTotalCount: () => adapter.getSnapshot().totalCount,
    };
    useImperativeHandle(ref, () => api);
    const { toolbar, rowMenu, hasRowActions } = useActions({
        actions,
        child: subTable
            ? {
                  actions: subTable.actions as unknown as
                      | TableAction<unknown>[]
                      | undefined,
                  selectedRows: childSelectedRows,
                  adapter:
                      childAdapter ??
                      (adapter as unknown as TableAdapter<unknown>),
                  api: api as unknown as TableApi<unknown>,
              }
            : undefined,
        expanded: rowExpanded,
        selectedRows: selection.rows,
        adapter,
        api,
    });

    const { adapter: controlAdapter, columns: controlColumns } =
        useControlTarget({
            adapter,
            columns,
            subTable,
            childAdapter,
            expanded: rowExpanded,
        });

    const select: SelectConfig<T> | undefined = selectionMode
        ? {
              mode: selectionMode,
              isSelected: selection.isSelected,
              canSelect: selection.canSelect,
              headerState: selection.headerState,
              onToggle: selection.toggle,
              onToggleAll: selection.toggleAll,
          }
        : undefined;

    const drag: DragConfig<T> | undefined = onRowDragEnd
        ? {
              enabled: () => true,
              onReorder: (from, to) => {
                  const rows = snapshot.rows;
                  const next = moveItem(rows, from, to);
                  if (next === rows) return;
                  onRowDragEnd({ row: rows[from], from, to, rows: next });
              },
          }
        : undefined;

    function renderExpandedRow(): ReactNode {
        if (!subTable || !childAdapter) return null;
        return (
            <NestedTable
                id={`${id}/${subTable.id ?? 'sub'}`}
                description={subTable.header ?? description}
                columns={subTable.columns}
                adapter={childAdapter}
                getRowId={subTable.getRowId ?? defaultGetRowId}
                selection={subTable.selection}
                emptyState={subTable.emptyState}
                onSelectionChange={(rows, ids) => {
                    setChildSelectedRows(rows);
                    subTable.onSelectionChange?.(rows, ids);
                }}
                arrayAdapter={arrayAdapter}
                __nested
            />
        );
    }

    const expand: ExpandConfig<T> | undefined = subTable
        ? {
              header: subTable.header,
              enabled: (row) =>
                  subTable.enabled ? subTable.enabled(row) : true,
              count: subTable.count,
              isExpanded: (row) => expansion.isExpanded(getRowId(row)),
              onToggle: (row) => {
                  expansion.toggle(getRowId(row));
              },
              render: renderExpandedRow,
          }
        : undefined;

    const onSort = (col: ResolvedColumn<T>): void => {
        adapter.setSort(nextSort(snapshot.sort, col.sortKey ?? col.id));
    };

    function renderRowMenu(row: T): ReactNode {
        return <RowMenu items={rowMenu(row)} label="Row actions" />;
    }

    const handleRowClick =
        onRowClick ??
        (selectionMode
            ? (row: T) => {
                  selection.toggle(row);
              }
            : undefined);

    const descId = `${id}-description`;
    const filtered =
        snapshot.search !== '' || snapshot.filters.some((f) => !f.locked);
    const staleError =
        snapshot.error && snapshot.rows.length > 0 ? (
            <StaleError error={snapshot.error} onRetry={adapter.refresh} />
        ) : undefined;

    return (
        <section aria-label={description} className="ics-table-root">
            <span id={descId} hidden>
                {description}
            </span>
            {}
            {__nested ? null : (
                <TableToolbar
                    actions={toolbar}
                    slot={toolbarSlot}
                    search={
                        hideSearch ? undefined : (
                            <TableSearch
                                adapter={controlAdapter}
                                columns={controlColumns}
                                placeholder={searchPlaceholder}
                            />
                        )
                    }
                />
            )}
            {}
            {staleError}
            <div className="ics-table__grid">
                <TableView
                    rows={snapshot.rows}
                    columns={gridColumns}
                    getRowId={getRowId}
                    describedBy={descId}
                    api={api}
                    sort={snapshot.sort}
                    onSort={onSort}
                    loading={snapshot.loading}
                    skeletonRowCount={
                        adapter.getPageSize?.() ?? PAGE_SIZE.table
                    }
                    empty={emptyContent(
                        snapshot.error,
                        snapshot.rows.length > 0,
                        emptyState,
                        filtered,
                        adapter.refresh,
                    )}
                    select={select}
                    expand={expand}
                    drag={drag}
                    rowMenu={hasRowActions ? renderRowMenu : undefined}
                    onRowClick={handleRowClick}
                    onResize={settings.setWidth}
                    collapsibleGroups={collapsibleGroups}
                    isGroupCollapsed={groupCollapse.isCollapsed}
                    onToggleGroup={groupCollapse.toggle}
                />
                <ColumnChooser
                    all={allCols}
                    hidden={hiddenIds}
                    settings={{
                        ...settings,
                        reset: () => {
                            settings.reset();
                            adapter.setFilters([]);
                            adapter.setSort(null);
                            adapter.setSearch('');
                        },
                    }}
                />
            </div>
            <TablePagination adapter={adapter} />
        </section>
    );
}

export const IcsDataTable = forwardRef(TableInner) as <T>(
    props: InternalTableProps<T> & { ref?: React.Ref<TableApi<T>> },
) => JSX.Element;

const NestedTable = forwardRef(TableInner) as <T>(
    props: InternalTableProps<T> & { ref?: React.Ref<TableApi<T>> },
) => JSX.Element;
